import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../commons/Toast';
import {
  fetchRules,
  saveRule,
  deleteRule,
  toggleRule,
  reorderRules,
  duplicateRule,
} from '../actions/rulesActions';

/**
 * Shared hook encapsulating all rule CRUD state and handlers.
 * Used by both RulesSection and AutomationRulesScreen.
 */
export default function useRuleManagement() {
  const dispatch = useDispatch();
  const { items: rules, loading, saving, error, saveErrors } = useSelector((state) => state.rules);
  const { showToast, toastMessage, showToastMessage, hideToast } = useToast();

  const [editingRule, setEditingRule] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const showEditor = editingRule !== null || isCreating;

  // ─── CRUD handlers ──────────────────────────────────────────────────────────

  const handleSave = async (ruleData) => {
    const result = await dispatch(saveRule(ruleData));
    if (result.success) {
      setEditingRule(null);
      setIsCreating(false);
    }
    return result;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(deleteRule(deleteTarget.id));
    if (result.success) {
      setDeleteTarget(null);
    }
  };

  const handleToggle = (ruleId, enabled) => {
    dispatch(toggleRule(ruleId, enabled));
  };

  const handleReorder = useCallback(async (orderedIds) => {
    const result = await dispatch(reorderRules(orderedIds));
    if (result?.success) {
      showToastMessage('Rule order saved');
    }
  }, [dispatch, showToastMessage]);

  const handleDuplicate = (ruleId) => {
    dispatch(duplicateRule(ruleId));
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setEditingRule(null);
    setIsCreating(true);
  };

  const handleStarterSelected = (template) => {
    setEditingRule({
      name: template.title,
      conditions_json: template.conditions_json,
      actions_json: template.actions_json,
      logic: template.logic,
    });
    setIsCreating(true);
  };

  const handleCancelEditor = () => {
    setEditingRule(null);
    setIsCreating(false);
  };

  const handleFetchRules = () => {
    dispatch(fetchRules());
  };

  return {
    // Redux state
    rules,
    loading,
    saving,
    error,
    saveErrors,

    // Local state
    editingRule,
    isCreating,
    deleteTarget,
    showEditor,

    // Toast
    showToast,
    toastMessage,
    hideToast,

    // Handlers
    handleSave,
    handleDelete,
    handleToggle,
    handleReorder,
    handleDuplicate,
    handleEdit,
    handleCreateNew,
    handleStarterSelected,
    handleCancelEditor,
    handleFetchRules,
    setDeleteTarget,
  };
}
